// gruppo/clipGruppo.ts — v042: video delle risate e foto anche nella partita a 4 (prima a fine partita c'era solo un
// messaggio di testo). Come nell'1 contro 1:
// - si fa SOLO con il mio sì (dato una volta in «Le tue scelte»); gli altri ci sono solo se hanno detto sì anche loro,
//   altrimenti al loro posto c'è un riquadro scuro con il «?» (lo sa il server: vista.clip e vista.foto);
// - il video resta sul telefono: si condivide o si scarica solo se chi gioca lo decide;
// - un no di qualcuno che si vede nel video lo butta subito (si rifà al round dopo).
// Il video è una griglia 2×2 (p1, p2 / p3, p4) e racconta l'ultima risata, quella che ha deciso chi vince.
import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from "react";
import { creaRegistratoreClip, ErroreClip, type RegistratoreClip } from "../clip/clip.ts";
import { tempoReale } from "../servizi/tempoReale.ts";
import { indirizzoSito } from "../gioco/Condividi.tsx";
import { caricaSimbolo, clipPossibili, opzioni, sceltaClip, type StatoClip } from "../gioco/usaClip.ts";
import { scattoVideo, type FotoPartita, type MomentoFoto } from "../gioco/fotoPartita.ts";
import { scelteAttuali } from "../servizi/scelte.ts";
import { stanzaGruppo } from "../gioco/stanzaGruppo.ts";
import { POSTI_GRUPPO, type Posto, type VistaGruppo } from "../../../condiviso/gruppo.ts";

/** Chi ha riso per ultimo (la risata che ha deciso) e chi ha vinto, dalla classifica di fine round. */
export function esitoGruppo(v: VistaGruppo): { riso: Posto | null; vinto: Posto | null } {
  const c = v.classifica ?? [];
  const primi = c.filter((x) => x.posizione === 1);
  const vinto = primi.length === 1 ? primi[0]!.posto : null;
  const secondi = c.filter((x) => x.posizione === 2 && x.motivo === "risata");
  return { riso: secondi.length === 1 ? secondi[0]!.posto : null, vinto };
}

export function useClipGruppo(p: {
  vista: VistaGruppo | null;
  io: Posto;
  /** il mio video in uscita (con il filtro) */
  invio: MediaStream;
  /** i video degli altri (videochiamata o bot) */
  remoti: Partial<Record<Posto, MediaStream | null>>;
  lingua: string;
  link: string | null;
}): { stato: StatoClip; crea: () => void; consenso: boolean; consensoAltro: boolean; disponibile: boolean } {
  const { vista, io, invio, remoti, lingua, link } = p;
  const disponibile = clipPossibili();
  const [stato, setStato] = useState<StatoClip>(disponibile ? { s: "attesa" } : { s: "spenta" });
  const reg = useRef<RegistratoreClip | null>(null);
  const urlRif = useRef<string | null>(null);
  const annulla = useRef<AbortController | null>(null);
  const simboloRif = useRef("");
  useEffect(() => {
    if (disponibile) void caricaSimbolo().then((svg) => (simboloRif.current = svg));
  }, [disponibile]);

  // la scelta fatta una volta vale anche qui: il sì al video e alle foto si manda da solo, una volta per stanza
  const mandato = useRef(false);
  useEffect(() => {
    if (!vista || mandato.current) return;
    mandato.current = true;
    if (disponibile && sceltaClip() === true && !vista.clip?.[io]) stanzaGruppo.consenso("clip", true);
    if (scelteAttuali().foto && !vista.foto?.[io]) stanzaGruppo.consenso("foto", true);
  }, [vista, io, disponibile]);

  const consenso = vista?.clip?.[io] === true;
  const consensoAltro = !!vista && POSTI_GRUPPO.some((x) => x !== io && vista.occupati[x] && vista.clip?.[x] === true);
  const fase = vista?.fase ?? "attesa";
  const round = vista?.round ?? 0;
  const invioRif = useRef(invio);
  invioRif.current = invio;
  const remotiRif = useRef(remoti);
  remotiRif.current = remoti;
  /** i posti nel video di questo round (chi ha detto sì quando è partito) */
  const dentro = useRef<Posto[]>([]);
  const roundRif = useRef<number | null>(null);
  const ev = useRef({ via: false, pausa: false, fine: false, risataT: null as number | null, faseVista: "" });

  const libera = useCallback(() => {
    annulla.current?.abort();
    reg.current?.libera();
    reg.current = null;
    dentro.current = [];
    if (urlRif.current) URL.revokeObjectURL(urlRif.current);
    urlRif.current = null;
  }, []);
  useEffect(() => libera, [libera]);

  // un round nuovo (il primo conto alla rovescia dopo il via): si riparte da capo
  useEffect(() => {
    if (!disponibile || !vista || fase !== "conto" || roundRif.current === round) return;
    roundRif.current = round;
    libera();
    ev.current = { via: false, pausa: false, fine: false, risataT: null, faseVista: "" };
    if (!consenso) {
      setStato({ s: "attesa" });
      return;
    }
    const conLui = (x: Posto) => x !== io && vista.occupati[x] && vista.clip?.[x] === true;
    dentro.current = POSTI_GRUPPO.filter((x) => x === io || conLui(x));
    const griglia = POSTI_GRUPPO.map((x) => (x === io ? invioRif.current : conLui(x) ? (remotiRif.current[x] ?? null) : null));
    const o = { ...opzioni(lingua, link ?? new URL(indirizzoSito()).host), simbolo: simboloRif.current, griglia: true };
    const r = creaRegistratoreClip({ sopra: null, sotto: invioRif.current, griglia }, o, { adesso: () => tempoReale.oraServer() });
    reg.current = r;
    r.avvia();
    setStato({ s: "registra" });
  }, [disponibile, vista, fase, round, consenso, io, lingua, link, libera]);

  // qualcuno che si vede nel video dice no (o io): il video del round si butta
  const chiaveSi = vista ? POSTI_GRUPPO.map((x) => (vista.clip?.[x] ? "1" : "0")).join("") : "";
  useEffect(() => {
    if (!reg.current || !vista) return;
    if (dentro.current.some((x) => vista.clip?.[x] !== true)) {
      libera();
      setStato({ s: "attesa" });
    }
  }, [chiaveSi, vista, libera]);

  // i video cambiano durante il round (il mio filtro, la videochiamata che arriva dopo)
  useEffect(() => {
    reg.current?.cambiaVideo?.("sotto", invio);
  }, [invio]);
  const chiaveRemoti = POSTI_GRUPPO.map((x) => (remoti[x] ? remoti[x]!.id : "-")).join(",");
  useEffect(() => {
    const r = reg.current;
    if (!r) return;
    POSTI_GRUPPO.forEach((x, k) => {
      if (x !== io && dentro.current.includes(x)) r.cambiaCella?.(k, remoti[x] ?? null);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chiaveRemoti, io]);

  // gli eventi del round (ora del server)
  useEffect(() => {
    const r = reg.current;
    if (!r || !vista) return;
    const ora = tempoReale.oraServer();
    const prima = ev.current.faseVista;
    ev.current.faseVista = fase;
    if (!ev.current.via && vista.tVia !== null && fase === "gioco") {
      ev.current.via = true;
      r.segna({ tipo: "via", t: vista.tVia });
    }
    // le pause e i conti alla rovescia brevi dopo una risata non entrano nel video
    const ferma = fase === "pausa" || (fase === "conto" && ev.current.via);
    if (ferma && !ev.current.pausa) {
      ev.current.pausa = true;
      r.segna({ tipo: "pausa", t: ora });
    } else if (!ferma && ev.current.pausa && fase === "gioco") {
      ev.current.pausa = false;
      r.segna({ tipo: "ripresa", t: ora });
    }
    if ((fase === "verifica" || fase === "accusa") && prima !== fase && prima !== "verifica" && prima !== "accusa") ev.current.risataT = ora;
    if (fase === "risultato" && !ev.current.fine) {
      ev.current.fine = true;
      const e = esitoGruppo(vista);
      const indice = (x: Posto | null) => (x ? POSTI_GRUPPO.indexOf(x) : null);
      if (e.riso) r.segna({ tipo: "risata", t: ev.current.risataT ?? ora, chi: "sopra" });
      r.segna({ tipo: "fine", t: ora, perdente: e.riso ? "sopra" : null, celle: { riso: indice(e.riso), vinto: indice(e.vinto) } });
      void r.ferma().then(() => setStato(e.riso ? { s: "pronta_da_creare" } : { s: "attesa" }));
    }
  }, [vista, fase]);

  const crea = useCallback(() => {
    const r = reg.current;
    if (!r) return;
    const c = new AbortController();
    annulla.current = c;
    setStato({ s: "creazione", avanzamento: 0 });
    r.creaClip((x) => setStato({ s: "creazione", avanzamento: x }), c.signal)
      .then((clip) => {
        if (urlRif.current) URL.revokeObjectURL(urlRif.current);
        urlRif.current = URL.createObjectURL(clip.blob);
        setStato({ s: "fatta", clip, url: urlRif.current, solo: dentro.current.length < 2 });
      })
      .catch((e: unknown) => setStato({ s: "errore", codice: e instanceof ErroreClip ? e.codice : "codifica" }));
  }, []);

  return { stato: disponibile ? stato : { s: "spenta" }, crea, consenso, consensoAltro, disponibile };
}

const PRIMA_DOPO_VIA_MS = 2500;
const OGNI_MS = 7000;
const MASSIMO_GIOCO = 2;

/**
 * Le foto della partita a 4: la mia faccia dalla fotocamera (senza filtro) e quella degli altri che hanno detto sì,
 * in una griglia 2×2. Se io non ho detto sì non si scatta niente. Restano nella memoria della pagina.
 */
export function useFotoGruppo(p: {
  vista: VistaGruppo | null;
  io: Posto;
  camera: MediaStream;
  video: RefObject<Partial<Record<Posto, HTMLVideoElement | null>>>;
}): { foto: FotoPartita[]; conAltri: boolean } {
  const { vista, io } = p;
  const [foto, setFoto] = useState<FotoPartita[]>([]);
  const videoCamera = useRef<HTMLVideoElement | null>(null);
  const mio = vista?.foto?.[io] === true;
  const chiaveAltri = vista ? POSTI_GRUPPO.map((x) => (x !== io && vista.occupati[x] && vista.foto?.[x] ? "1" : "0")).join("") : "";
  const conAltri = chiaveAltri.includes("1");
  const vistaRif = useRef(vista);
  vistaRif.current = vista;
  const mioRif = useRef(mio);
  mioRif.current = mio;

  useEffect(() => {
    const v = document.createElement("video");
    v.muted = true;
    v.playsInline = true;
    v.setAttribute("playsinline", "");
    v.srcObject = new MediaStream(p.camera.getVideoTracks());
    void v.play().catch(() => undefined);
    videoCamera.current = v;
    return () => {
      v.pause();
      v.srcObject = null;
      videoCamera.current = null;
    };
  }, [p.camera]);

  const scatta = (momento: MomentoFoto) => {
    const vi = vistaRif.current;
    if (!mioRif.current || !vi) return;
    const io2 = scattoVideo(videoCamera.current, true);
    if (!io2) return;
    const griglia = POSTI_GRUPPO.map((x) => (x === io ? io2 : vi.occupati[x] && vi.foto?.[x] ? scattoVideo(p.video.current?.[x] ?? null, false) : null));
    const nuova: FotoPartita = { id: `${momento}-${Date.now()}`, momento, io: io2, lui: null, griglia };
    setFoto((prima) => {
      if (momento === "inizio" && prima.some((f) => f.momento === "inizio")) return prima;
      if (momento !== "gioco") return [...prima.filter((f) => f.momento !== momento), nuova];
      const gioco = [...prima.filter((f) => f.momento === "gioco"), nuova].slice(-MASSIMO_GIOCO);
      return [...prima.filter((f) => f.momento !== "gioco"), ...gioco];
    });
  };
  const scattaRif = useRef(scatta);
  scattaRif.current = scatta;

  const fase = vista?.fase ?? null;
  const round = vista?.round ?? 0;
  const roundFoto = useRef<number | null>(null);
  const fasePrima = useRef(fase);
  useEffect(() => {
    const prima = fasePrima.current;
    fasePrima.current = fase;
    if (fase === "conto" && roundFoto.current !== round) {
      roundFoto.current = round;
      setFoto([]);
    }
    // la risata: appena il gioco si ferma per una risata (l'ultima foto «risata» è quella che ha deciso)
    if (prima === "gioco" && (fase === "verifica" || fase === "accusa" || fase === "risultato")) scattaRif.current("risata");
    if (fase !== "gioco") return;
    const primo = setTimeout(() => scattaRif.current("inizio"), PRIMA_DOPO_VIA_MS);
    const ogni = setInterval(() => scattaRif.current("gioco"), OGNI_MS);
    return () => {
      clearTimeout(primo);
      clearInterval(ogni);
    };
  }, [fase, round]);

  // qualcuno ritira il sì: la sua faccia sparisce subito dalle foto; io lo ritiro: niente foto
  useEffect(() => {
    const vi = vistaRif.current;
    if (!vi) return;
    setFoto((prima) =>
      prima.map((f) => (f.griglia ? { ...f, griglia: f.griglia.map((g, k) => (POSTI_GRUPPO[k] === io || (vi.occupati[POSTI_GRUPPO[k]!] && vi.foto?.[POSTI_GRUPPO[k]!]) ? g : null)) } : f)),
    );
  }, [chiaveAltri, io]);
  useEffect(() => {
    if (!mio) setFoto([]);
  }, [mio]);

  const ordinate = useMemo(() => {
    const ordine: Record<MomentoFoto, number> = { inizio: 0, gioco: 1, risata: 2 };
    return [...foto].sort((a, b) => ordine[a.momento] - ordine[b.momento]);
  }, [foto]);
  return { foto: ordinate, conAltri };
}
